// src/resources/verifications.js

import React, { useState } from 'react';
import { useTranslate } from 'react-admin';
import {
    List,
    useListContext,
    useNotify,
    useRefresh,
    Button,
    Title,
    fetchUtils,
    Link,
    Confirm,
    useRecordContext,
    useDataProvider
} from 'react-admin';

// --- تجميع استيرادات Material UI ---
import { 
    Card, 
    CardContent, 
    CardActions, 
    Typography, 
    Grid, 
    Box, 
    Tabs, 
    Tab, 
    Select, 
    MenuItem,
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    TextField as MuiTextField, // تعريف الاسم هنا مرة واحدة
    IconButton,
    Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Cancel';

import { API_URL } from '../config';

// --- 1. زر الموافقة المباشرة (للسيارات) ---
const ApproveButton = ({ record }) => {
    const notify = useNotify();
    const refresh = useRefresh();

    const handleClick = () => {
        const resource = record.type === 'USER' ? 'users' : 'cars';
        const token = localStorage.getItem('token');

        fetchUtils.fetchJson(`${API_URL}/admin/${resource}/${record.originalId}/verify`, {
            method: 'PUT',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
             }),
            body: JSON.stringify({ status: 'APPROVED' }),
        })
        .then(() => {
            notify('Verification approved', { type: 'success' });
            refresh();
        })
        .catch((error) => {
            notify(`Error approving: ${error.message || 'An error occurred'}`, { type: 'warning', autoHideDuration: 5000 });
        });
    };

    return <Button label="Approve" onClick={handleClick} color="primary" />;
};

// --- 2. زر المراجعة والموافقة (للمستخدمين - الجديد) ---
const ReviewVerificationButton = ({ record }) => {
    const [open, setOpen] = useState(false);
    const [fullName, setFullName] = useState(record.fullName || '');
    const [nationalId, setNationalId] = useState(record.nationalId || '');
    const [address, setAddress] = useState(record.address || '');
    
    // --- [إضافة] المتغيرات الجديدة ---
    const [jobTitle, setJobTitle] = useState(record.jobTitle || '');
    const [maritalStatus, setMaritalStatus] = useState(record.maritalStatus || '');
    // -----------------------------
    
    const notify = useNotify();
    const refresh = useRefresh();

    const handleOpen = () => {
        setFullName(record.fullName || '');
        setNationalId(record.nationalId || '');
        setAddress(record.address || '');
        // تعيين القيم عند الفتح
        setJobTitle(record.jobTitle || '');
        setMaritalStatus(record.maritalStatus || '');
        setOpen(true);
    };
    
    const handleClose = () => setOpen(false);

    const handleApprove = () => {
        // التحقق من الحقول الأساسية (يمكنك إضافة الحقول الجديدة هنا إذا كانت إجبارية)
        if (!fullName || !nationalId || !address) {
            notify('Please fill in Name, ID, and Address.', { type: 'warning' });
            return;
        }

        const token = localStorage.getItem('token');
        fetchUtils.fetchJson(`${API_URL}/admin/users/${record.originalId}/verify`, {
            method: 'PUT',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
            body: JSON.stringify({ 
                status: 'APPROVED', 
                fullName, 
                nationalId, 
                address,
                // --- [إضافة] إرسال البيانات الجديدة ---
                jobTitle,
                maritalStatus
                // -----------------------------------
            }),
        })
        .then(() => {
            notify('User verified and data saved', { type: 'success' });
            setOpen(false);
            refresh();
        })
        .catch((error) => {
            notify(`Error: ${error.message}`, { type: 'warning' });
        });
    };

    if (record.type !== 'USER') return null;

    return (
        <>
            <Button label="Review & Approve" onClick={handleOpen} color="primary" variant="contained" sx={{ mr: 1 }} />
            
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle>Review Identity Documents</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                            <ImageBox title="ID Front" imageUrl={record.idFrontImageUrl} />
                        </Grid>
                        <Grid item xs={6}>
                            <ImageBox title="ID Back" imageUrl={record.idBackImageUrl} />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Extract Data</Typography>
                            <Typography variant="caption" color="textSecondary">
                                Verify user input and write data from the ID image.
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <MuiTextField
                                label="Full Name (الاسم الرباعي)"
                                fullWidth
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <MuiTextField
                                label="National ID (الرقم القومي)"
                                fullWidth
                                value={nationalId}
                                onChange={(e) => setNationalId(e.target.value)}
                            />
                        </Grid>
                        
                        {/* --- [إضافة] الحقول الجديدة في الواجهة --- */}
                        <Grid item xs={12} sm={6}>
                            <MuiTextField
                                label="Job Title (المهنة)"
                                fullWidth
                                value={jobTitle}
                                onChange={(e) => setJobTitle(e.target.value)}
                                placeholder="ex: Engineer, Student..."
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <MuiTextField
                                label="Marital Status (الحالة الاجتماعية)"
                                fullWidth
                                value={maritalStatus}
                                onChange={(e) => setMaritalStatus(e.target.value)}
                                placeholder="ex: Single, Married..."
                            />
                        </Grid>
                        {/* -------------------------------------- */}

                        <Grid item xs={12}>
                            <MuiTextField
                                label="Address (العنوان من البطاقة)"
                                fullWidth
                                multiline
                                rows={2}
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                placeholder="Write address here..."
                                required
                                sx={{ backgroundColor: '#f9fbe7' }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button label="Cancel" onClick={handleClose} />
                    <Button label="Confirm & Save" onClick={handleApprove} color="primary" />
                </DialogActions>
            </Dialog>
        </>
    );
};

// --- 3. زر الرفض ---
const RejectButton = ({ record }) => {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const notify = useNotify();
    const refresh = useRefresh();
    const translate = useTranslate();

    const reasonKeys = [
        "unclear_id_license_photo",
        "expired_document",
        "information_mismatch",
        "incomplete_documents",
        "profile_picture_unclear_mismatch",
        "invalid_plate_number_format",
        "other",
    ];

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setReason('');
        setCustomReason('');
    };

    const handleReject = () => {
        let finalReason = '';
        if (reason === 'other') {
            if (!customReason.trim()) {
                notify(translate('resources.verifications.errors.customReasonRequired'), { type: 'warning' });
                return;
            }
            finalReason = customReason.trim();
        } else if (!reason) {
           notify(translate('resources.verifications.errors.reasonRequired'), { type: 'warning' });
            return;
        } else {
           finalReason = reason; 
        }

        const resource = record.type === 'USER' ? 'users' : 'cars';
        const token = localStorage.getItem('token');

        fetchUtils.fetchJson(`${API_URL}/admin/${resource}/${record.originalId}/verify`, {
            method: 'PUT',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
            body: JSON.stringify({ status: 'REJECTED', reason: finalReason }),
        })
        .then(() => {
            notify('Verification rejected', { type: 'info' });
            handleClose();
            refresh();
        })
        .catch((error) => {
            notify(`Error rejecting: ${error.message || 'An error occurred'}`, { type: 'warning', autoHideDuration: 5000 });
        });
    };

    return (
        <>
            <Button label="Reject" onClick={handleOpen} color="warning" />
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
                <DialogTitle>{translate('resources.verifications.rejectTitle')}</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Select or provide a reason for rejecting the verification.
                    </Typography>

                    <Select
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        displayEmpty
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        sx={{ mt: 2 }}
                    >
                        <MenuItem value="" disabled>
                            {translate('resources.verifications.selectReason')}
                        </MenuItem>
                        {reasonKeys.map((key) => (
                           <MenuItem key={key} value={key}> 
                               {translate(`resources.verifications.reasons.${key}`)}
                           </MenuItem>
                        ))}
                    </Select>

                    {reason === 'other' && (
                        <MuiTextField
                            autoFocus
                            margin="dense"
                            label={translate('resources.verifications.customReasonLabel')}
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={customReason}
                            onChange={(e) => setCustomReason(e.target.value)}
                            sx={{ mt: 2 }}
                            multiline
                            rows={2}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} label={translate('ra.action.cancel')} />
                    <Button onClick={handleReject} label={translate('resources.verifications.confirmReject')} color="warning" />
                </DialogActions>
            </Dialog>
        </>
    );
};

// --- 4. صندوق الصورة ---
const ImageBox = ({ title, imageUrl }) => (
    <Box textAlign="center" mb={2}>
        <Typography variant="subtitle2" gutterBottom>{title}</Typography>
        {imageUrl ? (
            <a href={imageUrl} target="_blank" rel="noopener noreferrer">
                <img
                    src={imageUrl}
                    alt={title}
                    style={{
                        maxWidth: '100%',
                        maxHeight: 180,
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        objectFit: 'contain'
                    }}
                 />
            </a>
        ) : (
            <Typography variant="body2" color="textSecondary">Not provided</Typography>
        )}
    </Box>
);

// --- 5. الشبكة (Grid) ---
const VerificationGrid = ({ records, type }) => {
    if (!records || records.length === 0) {
        return <Typography sx={{ padding: 2 }}>No pending {type.toLowerCase()} verifications.</Typography>;
    }

    return (
        <Grid container spacing={2} sx={{ marginTop: '1em', padding: '0 16px' }}>
            {records.map(record => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={record.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                         <CardContent sx={{ flexGrow: 1 }}>
                            <Link to={`/users/${record.type === 'USER' ? record.originalId : record.user?.id}`}>
                                <Typography variant="h6" gutterBottom>
                                     {record.name || record.user?.name || 'Unknown'}
                                </Typography>
                            </Link>
                             <Typography variant="body2" color="textSecondary" gutterBottom>
                                {record.type === 'USER' ? `User ID Verification` : `Car Verification`}
                            </Typography>

                            {record.type === 'USER' ? (
                                <>
                                    {/* عرض البيانات النصية المبدئية */}
                                    {(record.fullName || record.nationalId) && (
                                        <Box sx={{ mb: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                            <Typography variant="caption" display="block"><strong>Input Name:</strong> {record.fullName || '-'}</Typography>
                                            <Typography variant="caption" display="block"><strong>Input ID:</strong> {record.nationalId || '-'}</Typography>
                                        </Box>
                                    )}
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}><ImageBox title="Profile Picture" imageUrl={record.profileImage} /></Grid>
                                        <Grid item xs={6}><ImageBox title="ID Front" imageUrl={record.idFrontImageUrl} /></Grid>
                                        <Grid item xs={12}><ImageBox title="ID Back" imageUrl={record.idBackImageUrl} /></Grid>
                                    </Grid>
                                </>
                            ) : (
                                <>
                                    <Typography variant="caption" display="block">Brand: {record.brand}</Typography>
                                     <Typography variant="caption" display="block">Model: {record.model} ({record.year})</Typography>
                                    <Typography variant="caption" display="block">Color: {record.color}</Typography>
                                    <Typography variant="caption" display="block" sx={{ mb: 1, fontWeight: 'bold' }}>Plate: {record.plate}</Typography>
                                     <Grid container spacing={1}>
                                         <Grid item xs={6}><ImageBox title="Driving License" imageUrl={record.user?.drivingLicenseUrl} /></Grid>
                                         <Grid item xs={12} sm={6}><ImageBox title="Car License Front" imageUrl={record.carLicenseFrontUrl} /></Grid>
                                         <Grid item xs={12} sm={6}><ImageBox title="Car License Back" imageUrl={record.carLicenseBackUrl} /></Grid>
                                     </Grid>
                                </>
                            )}
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'flex-end', borderTop: '1px solid #eee', mt: 'auto' }}>
                            <RejectButton record={record} />
                            
                            {/* استخدام الزر المناسب حسب النوع */}
                            {record.type === 'USER' ? (
                                <ReviewVerificationButton record={record} />
                            ) : (
                                <ApproveButton record={record} />
                            )}
                        </CardActions>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

// --- 6. مكونات القائمة والتبويبات ---
export const VerificationList = () => (
    <List title="Verification Requests" actions={null} perPage={100} component="div" sx={{ mt: -3 }}>
        <VerificationTabs />
    </List>
);

const VerificationTabs = () => {
    const { data, isLoading, error } = useListContext();
    const [tab, setTab] = useState(0);

    if (isLoading) return <Typography sx={{ padding: 2 }}>Loading...</Typography>;
    if (error) return <Typography sx={{ padding: 2, color: 'error.main' }}>Error loading verification requests.</Typography>;

    const userRequests = data ? data.filter(r => r.type === 'USER') : [];
    const carRequests = data ? data.filter(r => r.type === 'CAR') : [];

    return (
        <>
            <Title title="Verification Requests" />
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
                <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)} variant="fullWidth">
                    <Tab label={`Identity (${userRequests.length})`} />
                    <Tab label={`Cars (${carRequests.length})`} />
                </Tabs>
            </Box>
            {tab === 0 && <VerificationGrid records={userRequests} type="USER" />}
            {tab === 1 && <VerificationGrid records={carRequests} type="CAR" />}
        </>
    );
};